import { ShieldCheck, GraduationCap, Wallet, FileText } from 'lucide-react';
import StudentVisaGuideShell from './components/StudentVisaGuideShell';

/**
 * Ireland D Study Visa — config consumed by the shared StudentVisaGuideShell.
 * All rendering logic lives in the shell. Edit data here.
 */

const CONFIG = {
  testidPrefix: 'ie-visa',

  seo: {
    title: 'Ireland Student Visa 2026 Guide for Indian Students | Zafco Study Abroad',
    description:
      'Master the Ireland D Study Visa process. Learn about the €10,000 living cost rule, document checklists, and 2026 financial requirements for Indian students.',
  },

  breadcrumb: [
    { name: 'Home',         url: 'https://zafco.in/' },
    { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
    { name: 'Ireland',      url: 'https://zafco.in/study-abroad/ireland/' },
    { name: 'Student Visa Guide' },
  ],

  hero: {
    eyebrow: 'Ireland Visa Guide · 2026',
    lastUpdated: '30 May 2026',
    titlePre: 'Ireland Student Visa for Indian Students',
    titleAccent: '2026',
    summaryHtml:
      'The complete roadmap for Indian undergraduate and postgraduate students applying for the <strong>Long Stay D Study Visa</strong> to Ireland. Updated for 2026 — including the <strong>€10,000 living-cost rule</strong>, the <strong>€6,000 minimum tuition payment</strong>, and the AVATS online process.',
    primaryCta: 'Speak with an Ireland Expert',
    primaryHref: '/study-abroad/book-counselling/?source=study-abroad-ireland-student-visa',
    secondaryCta: 'Evaluate My Profile',
    secondaryHref: '/study-abroad/free-profile-evaluation/?source=study-abroad-ireland-student-visa-hero&country=ireland',
  },

  // €10,000 Financial Alert — high-visibility snapshot card (the user-requested "Financial Alert / Pro-Tip box")
  snapshot: {
    eyebrow: '2026 Living-Cost Requirement',
    amount: '€10,000',
    subLabel: 'Per academic year · in addition to tuition',
    paragraphsHtml: [
      "<strong>The single most scrutinised figure in your Ireland D Study Visa file.</strong> You must show at least <strong>€10,000</strong> in accessible funds to cover the first year's living expenses — entirely separate from tuition. For shorter programs (6–8 months), Ireland applies a monthly calculation (~€833 / month).",
      'Alongside this, most degree applicants must prove the <strong>first-year tuition or a minimum €6,000 deposit</strong> has already been paid before the visa is lodged. Funds must be liquid and accessible, not locked in fixed-term instruments that mature after travel.',
    ],
  },

  // C vs D Study Visa Comparison Table — re-uses the generic providers table
  providers: {
    heading: {
      eyebrow: 'Pick the Right Route',
      title: 'C vs D Study Visa Comparison',
      subtitle:
        'Ireland offers two student visa categories for non-EU/EEA nationals. Almost every Indian degree applicant lodges the Long Stay D — the C is only suitable for short language/training courses.',
    },
    columns: [
      { key: 'feature',  label: 'Feature',                    bold: true },
      { key: 'shortC',   label: 'C Study Visa (Short Stay)' },
      { key: 'longD',    label: 'D Study Visa (Long Stay)' },
    ],
    rows: [
      { feature: 'Course duration',        shortC: 'Under 90 days',                             longD: '90+ days — full degrees, higher diplomas' },
      { feature: 'Typical use case',       shortC: 'Language schools, summer schools, training', longD: 'Bachelor\u2019s, master\u2019s, foundation programs' },
      { feature: 'Conversion to long-term',shortC: 'Not permitted — must leave Ireland',         longD: 'IRP issued on arrival; permits multi-year stay' },
      { feature: 'Part-time work rights',  shortC: 'Not allowed',                                longD: 'Up to 20 hrs/wk in term; 40 hrs/wk in holidays' },
      { feature: 'Post-study work option', shortC: 'None',                                       longD: 'Third Level Graduate Programme (1–2 yrs)' },
      { feature: 'ILEP listing required',  shortC: 'No (course just needs to be recognised)',    longD: 'Yes — institution must be on the ILEP register' },
    ],
  },

  // 2026 compliance — the €10K + €6K rules as policy alerts (high-visibility)
  policyAlerts: {
    heading: {
      eyebrow: 'Critical 2025–2026 Compliance Rules',
      title: 'What Will Get Your File Refused',
      subtitle:
        'Ireland tightened financial-proof rules for long-stay students in the last two cycles. These are the two non-negotiables for any 2026 D Study Visa application from India.',
    },
    items: [
      {
        date: '€10,000+',
        title: 'Living-Cost Threshold (Annual)',
        body:
          'You must show ≥ €10,000 in accessible funds covering the first academic year — separate from tuition. For 6–8 month programs, Ireland uses a ~€833 / month calculation up to a ceiling of approximately €6,665. Funds must be liquid and recently established (not parked deposits that surface only in the visa cycle).',
      },
      {
        date: '€6,000+',
        title: 'Minimum Tuition Pre-Payment',
        body:
          'Most degree applicants must pay at least the first-year fee or a substantial deposit — commonly around €6,000 or more — before lodging the visa. Receipts must match the university\u2019s own records exactly. Mismatched payment evidence is one of the top three refusal reasons.',
      },
    ],
  },

  // Eligibility & timeline backwards-plan from intake
  timeline: {
    heading: {
      eyebrow: 'When to Start',
      title: 'Eligibility & Backwards-Plan Timeline',
      subtitle:
        'Apply 8–12 weeks before your course start date — D Study processing typically runs 8–10 weeks and adverse-information requests can extend that by 2–3 weeks.',
    },
    rows: [
      { window: '6 months before intake', action: 'Apply to ILEP-listed Irish institutions; secure an unconditional Letter of Acceptance.' },
      { window: '4–5 months before',      action: 'Pay tuition deposit (≥ €6,000) and gather six-month bank statements for student + sponsor.' },
      { window: '3–4 months before',      action: 'Sit IELTS / TOEFL / PTE; lock in English score that meets institution + visa requirements.' },
      { window: '2–3 months before',      action: 'Lodge AVATS online application; pay visa fee; book biometrics at the visa application centre.' },
      { window: '1–2 months before',      action: 'Submit document set with cover letter; respond to any additional-information requests within 7 days.' },
      { window: '8–10 weeks after',       action: 'Visa outcome issued. Plan flight, accommodation, and IRP registration timeline post-arrival.' },
    ],
  },

  // Detailed Financial Documentation Checklist + Speak with an Ireland Expert CTA
  financialProof: {
    heading: {
      eyebrow: 'Money in the Bank',
      title: 'Financial Evidence Breakdown',
      subtitle:
        'Ireland treats your financial file as a credibility test. We layer multiple sources — student account, sponsor income, tuition receipts, FD evidence — so the visa officer sees one coherent narrative.',
    },
    rows: [
      { item: 'Living costs (12-month academic year)',         amount: '€10,000+',           note: 'Accessible funds; separate from tuition.' },
      { item: 'Living costs (6–8 month programs)',             amount: '€833 / month',       note: 'Up to a ceiling of approximately €6,665.' },
      { item: 'Tuition pre-payment (deposit / first year)',    amount: '€6,000+',            note: 'University receipt must match official records.' },
      { item: 'Bank statements (student + sponsor)',           amount: '6 months',           note: 'Transactions, not just closing balances.' },
      { item: 'Health insurance (Ireland-valid)',              amount: 'Course duration',     note: 'Mandatory; often €150–€600 / year.' },
      { item: 'Return travel allowance',                       amount: '€500 – €800',         note: 'Recommended, not a hard floor.' },
    ],
    cta: {
      tag: 'Financial Setup',
      heading: 'Speak with an Ireland Expert — Financial File Audit',
      copy:
        'ZAFCO Ireland counsellors run a three-pass audit on every D Study file — accessible-funds threshold, tuition-receipt matching, and sponsor narrative — before you ever click "Submit" in AVATS.',
      button: 'Speak with an Ireland Expert',
      href: '/study-abroad/book-counselling/?source=study-abroad-ireland-student-visa-cta-financial',
      testid: 'ie-visa-cta-financial',
    },
  },

  // Documents Checklist — interactive Core / Financial / Academic / Health cards
  checklists: {
    heading: {
      eyebrow: 'Zero-Error Edition',
      title: 'Ireland D Study Visa Document Checklist',
      subtitle:
        'Four categories. Submit through the visa application centre (VFS) along with your AVATS summary sheet. Disorganised files often trigger 2–3 week extensions.',
    },
    cards: [
      {
        tag: 'Core',
        Icon: ShieldCheck,
        tone: 'blue',
        items: [
          'Valid passport — ≥ 12 months validity from intended arrival',
          'Copies of any previous passports + visa pages',
          'Completed AVATS online application + signed summary sheet',
          'Letter of Acceptance from an ILEP-listed institution (course, NFQ level, start date, fees)',
          'Proof of tuition fee payment (receipts / institution confirmation)',
        ],
      },
      {
        tag: 'Financial',
        Icon: Wallet,
        tone: 'emerald',
        items: [
          '6-month bank statements (student + sponsor) with clear transaction history',
          '€10,000+ living-cost evidence in accessible funds',
          'Salary slips / tax returns / business proofs for sponsor income',
          'Notarised sponsorship letter (if parents / relatives funding)',
          'Explanation + supporting docs for any large recent deposits',
        ],
      },
      {
        tag: 'Academic',
        Icon: GraduationCap,
        tone: 'gold',
        items: [
          'Class 10 & 12 mark sheets (undergrad applicants)',
          'Bachelor\u2019s degree + semester-wise transcripts (postgrad applicants)',
          'English-test results — IELTS 6.0–6.5 / TOEFL / PTE (valid at time of application)',
          'Statement of Purpose tied to your career plan',
          'Letters of Recommendation (where required by institution)',
        ],
      },
      {
        tag: 'Additional',
        Icon: FileText,
        tone: 'amber',
        items: [
          'Health / medical insurance valid in Ireland for course duration',
          'Biometrics enrolment at the visa application centre',
          'Passport-size photographs to Irish specifications',
          'Explanation for any study or work gaps (with supporting docs)',
          'Cover letter sequencing the file in a logical narrative order',
        ],
      },
    ],
  },

  // 6-step Process Timeline
  steps: {
    heading: {
      eyebrow: 'Process Flow',
      title: 'The 6-Step Ireland D Study Visa Application',
      subtitle: 'Linear, auditable, and tracked. Each step has a single deliverable so nothing parallel-fails.',
    },
    items: [
      { title: 'Course & Offer Confirmation', desc: 'Apply to ILEP-listed Irish institutions; accept an unconditional offer and pay the minimum tuition deposit.' },
      { title: 'Financial & Document Preparation', desc: 'Arrange the €10,000+ living-cost evidence, 6-month bank statements, sponsor proofs, and Ireland-valid medical insurance.' },
      { title: 'Online Application & Fee Payment', desc: 'Complete the AVATS online form for the Long Stay D Study Visa, pay the visa fee, and generate the signed summary sheet.' },
      { title: 'Biometrics & Document Submission', desc: 'Book the visa application centre appointment (e.g., VFS); submit biometrics and the full file with a clear cover letter.' },
      { title: 'Visa Processing & Outcome', desc: 'Typical processing 8–10 weeks. Respond to any additional-information requests promptly to avoid stalls.' },
      { title: 'Arrival in Ireland & IRP Registration', desc: 'Once granted, travel, attend orientation, then register with Irish immigration to obtain your Irish Residence Permit (IRP).' },
    ],
  },

  // Post-arrival + Work Rights summary
  postArrival: {
    heading: {
      eyebrow: 'After Landing',
      title: 'First-30-Days Checklist + Work Rights',
      subtitle: 'What every Indian student must complete after landing in Dublin / Cork / Galway to stay compliant.',
    },
    items: [
      'Register with Irish immigration and obtain your Irish Residence Permit (IRP).',
      'Open an Irish bank account (AIB / Bank of Ireland student account).',
      'Activate part-time work rights — up to 20 hrs/wk in term, 40 hrs/wk in holidays.',
      'Confirm accommodation lease and add it to your IRP file.',
      'Plan for the Third Level Graduate Programme post-graduation (1–2 yrs post-study work).',
    ],
  },

  faqsHeading: {
    eyebrow: 'Frequently Asked',
    title: 'Ireland D Study Visa FAQs',
    subtitle: 'Schema-marked answers to the six most-asked questions from Indian families in 2026.',
  },
  faqs: [
    {
      q: 'How much bank balance is required for an Ireland student visa in 2026?',
      a: 'You must generally show that you have at least €10,000 for living costs for a full academic year, plus proof of tuition fee payment for your course.',
    },
    {
      q: 'Is the €10,000 living cost requirement separate from tuition fees?',
      a: 'Yes. Living costs are in addition to tuition fees and must be shown as funds that you can access during your stay.',
    },
    {
      q: 'How early should I apply for an Ireland D Study Visa from India?',
      a: 'It is advisable to apply 8–12 weeks before your course start date to allow time for processing and any additional queries.',
    },
    {
      q: 'Can I work while studying in Ireland on a D Study Visa?',
      a: 'Eligible students at recognised institutions can usually work part time during term and more hours during defined holiday periods under current rules — up to 20 hours per week in term and up to 40 hours per week during designated holiday periods.',
    },
    {
      q: 'What are common reasons for Ireland student visa refusals?',
      a: 'Frequent reasons include insufficient or unclear financial proof, inconsistent study plans, weak documentation, and unexplained large deposits in bank statements.',
    },
    {
      q: 'How does Zafco improve Ireland visa success?',
      a: 'Zafco manages your file end to end: course fit, financial structuring, documentation, and narrative alignment, treating your Ireland visa as part of a data-driven migration plan, not an isolated application.',
    },
  ],

  anchorLinks: {
    heading: {
      eyebrow: 'Explore Further',
      title: 'Related Ireland Resources',
      subtitle: 'Course choice, cost of study, work rights, and post-study career planning — everything in one cluster.',
    },
    items: [
      { label: 'Study in Ireland',                  path: '/study-abroad/ireland/' },
      { label: 'Cost of Studying in Ireland',       path: '/study-abroad/ireland/cost-of-study/' },
      { label: 'Best Universities in Ireland',      path: '/study-abroad/ireland/best-universities/' },
      { label: 'Work Rights in Ireland',            path: '/study-abroad/ireland/work-rights/' },
      { label: 'Free Profile Evaluation',           path: '/study-abroad/free-profile-evaluation/?source=study-abroad-ireland-student-visa-anchor' },
      { label: 'Book a Free Counselling Session',   path: '/study-abroad/book-counselling/?source=study-abroad-ireland-student-visa-anchor' },
    ],
  },

  finalCta: {
    title: 'Lodge your Ireland D Study Visa with zero surprises.',
    subtitle:
      'Our ILEP-aware counsellors run a 3-pass audit on every Ireland file — financial credibility, tuition-payment matching, and narrative alignment — before AVATS submission.',
    whatsappText: 'Hello ZAFCO - I want help with the Ireland D Study Visa.',
    counsellingSource: 'study-abroad-ireland-student-visa-final-cta',
  },
};

const IrelandStudentVisaPage = () => <StudentVisaGuideShell config={CONFIG} />;
export default IrelandStudentVisaPage;
